import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Timer, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const fmtMoney = (cents, currency = 'USD') => {
  if (cents == null) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100); }
  catch { return `$${(cents / 100).toFixed(2)}`; }
};

const ageLabel = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const h = ms / 3.6e6;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m ago`;
  if (h < 24) return `${Math.round(h)}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const isPast = (iso, hours) => {
  if (!iso) return false;
  return (Date.now() - new Date(iso).getTime()) >= hours * 3600 * 1000;
};

export default function PendingOrdersPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [expiring, setExpiring] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/orders/pending?limit=200');
      setRows(data.orders || []);
      setTotal(data.total || 0);
      setSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load pending orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleAllOn = (list) => {
    setSelected(new Set(list.map(r => r.id)));
  };

  const expireSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Mark ${selected.size} pending order(s) as expired?`)) return;
    setExpiring(true);
    try {
      const { data } = await api.post('/admin/orders/mark-expired', { order_ids: [...selected] });
      toast.success(`Expired ${data.expired} order(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk expire failed');
    } finally { setExpiring(false); }
  };

  const expireAllPast24h = async () => {
    if (!window.confirm('Mark ALL pending orders older than 24 hours as expired?')) return;
    setExpiring(true);
    try {
      const { data } = await api.post('/admin/orders/mark-expired', { expire_all_older_than_hours: 24 });
      toast.success(`Expired ${data.expired} order(s) older than 24h`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk expire failed');
    } finally { setExpiring(false); }
  };

  const stalePast24 = useMemo(() => rows.filter(r => isPast(r.created_at, 24)), [rows]);

  if (loading) return (
    <div className="flex items-center justify-center py-12" data-testid="pending-loading">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="pending-orders-page">
      <div className="flex items-center gap-2">
        <Timer className="h-4 w-4 text-ink" />
        <h1 className="text-base font-semibold text-ink">Pending Orders</h1>
        <span className="text-xs text-muted-foreground">({total} total{stalePast24.length ? ` · ${stalePast24.length} past 24h` : ''})</span>
      </div>

      <Card className="bg-white border-stone/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <p className="text-[11px] text-muted-foreground max-w-md">
              Unpaid checkouts. Auto-expire runs every 15 minutes for orders past 24h;
              use the buttons here to expire on demand.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                onClick={() => toggleAllOn(stalePast24)}
                disabled={stalePast24.length === 0}
                data-testid="select-stale-btn">
                Select {stalePast24.length} past 24h
              </Button>
              <Button size="sm" variant="destructive" className="h-7 text-xs gap-1"
                onClick={expireSelected} disabled={selected.size === 0 || expiring}
                data-testid="expire-selected-btn">
                <Trash2 className="h-3 w-3" />
                Mark {selected.size || ''} abandoned as expired
              </Button>
              <Button size="sm" className="h-7 text-xs gap-1"
                onClick={expireAllPast24h} disabled={expiring}
                data-testid="expire-all-24h-btn">
                <AlertTriangle className="h-3 w-3" />
                Expire ALL past 24h
              </Button>
            </div>
          </div>

          {rows.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No pending orders. All caught up.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-stone/30">
                  <th className="w-8 pb-2"></th>
                  <th className="pb-2">Order</th>
                  <th className="pb-2">Customer</th>
                  <th className="pb-2">Items</th>
                  <th className="pb-2 text-right">Total</th>
                  <th className="pb-2">Age</th>
                  <th className="pb-2">Reminded</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const stale = isPast(r.created_at, 24);
                  return (
                    <tr key={r.id} className="border-b border-stone/10 hover:bg-stone/5" data-testid={`pending-row-${r.id}`}>
                      <td className="py-1.5">
                        <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleOne(r.id)} data-testid={`select-${r.id}`} />
                      </td>
                      <td className="py-1.5 font-mono text-[10px] text-ink">{r.order_number}</td>
                      <td className="py-1.5">{r.customer_email || '—'}</td>
                      <td className="py-1.5">{r.items_count}</td>
                      <td className="py-1.5 text-right">{fmtMoney(r.total_cents, r.currency)}</td>
                      <td className={`py-1.5 ${stale ? 'text-[#5A1F2A] font-semibold' : ''}`}>{ageLabel(r.created_at)}</td>
                      <td className="py-1.5 text-[10px] text-muted-foreground">{r.abandon_reminder_sent_at ? '✓' : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
